/* ==========================================================================
   NWP ACCOUNT — sign-in panel enhancement layer
   Composes with Prestige 11.4.0. The header panel is a Prestige
   <dropdown-menu-disclosure>, so Prestige keeps ownership of open/close,
   hover trigger + 250ms tolerance, sibling-menu handoff, Escape, focus-out
   and the [open] removal after its hide animation (upgraded by nwp-header.js).

   Shared pop-out geometry and its hover bridge are owned by nwp-header.js.
   This module adds only what a *form* inside a hover menu needs:
   - email normalization + validation with an inline, announced error;
   - a submitting state (the redirect to Shopify's passwordless sign-in is a
     plain GET navigation: /customer_authentication/login?login_hint=…&
     login_hint_mode=submit, so the hosted flow sends the code at once and
     opens its code step — the same parameters Shopify's account sheet sends);
   - "engaged" protection: while the email field is focused or holds text,
     the pointer leaving the panel no longer closes it, and a click outside
     closes it explicitly instead;
   - deterministic keyboard entry into the panel (Tab from the open trigger,
     or Enter/Space on the trigger, lands on the email field);
   - idempotent init across shopify:section:load re-renders.

   Progressive enhancement: without this file the <details> still opens
   natively and the form still submits with native validation.
   ========================================================================== */

const HOVER_TOLERANCE_MS = 250; // Prestige MenuDisclosure.mouseOverDelayTolerance
const initialized = new WeakSet();
const recoveryTimers = new WeakMap();
const recoveryDeadlines = new WeakMap();

function resetSubmission(form) {
  clearTimeout(recoveryTimers.get(form));
  recoveryDeadlines.delete(form);
  form.classList.remove('is-submitting');
  const submit = form.querySelector('.nwp-account__submit');
  if (submit) { submit.disabled = false; submit.removeAttribute('aria-busy'); }
}

const finePointer = window.matchMedia('screen and (pointer: fine)');

/* :focus-visible in matches() throws on engines that lack the selector —
   probe once, degrade to plain :focus (same guard as nwp-header.js). */
const focusVisibleSelector = (() => {
  try {
    document.documentElement.matches(':focus-visible');
    return ':focus-visible';
  } catch {
    return ':focus';
  }
})();

function setReturnTo(form) {
  const field = form.querySelector('input[name="return_to"]');
  if (!field) return;
  /* Shopify only honours relative return_to values; keep the query so
     collection filters / pagination survive the round trip. */
  const safePath = `/${window.location.pathname.replace(/^\/+/, '')}`;
  field.value = `${safePath}${window.location.search}`;
}

function setPlatformRouteParameters(form) {
  form.querySelectorAll('[data-nwp-account-route-param]').forEach((field) => field.remove());

  const routeValue = form.dataset.loginUrl;
  if (!routeValue) return;

  let route;
  try {
    route = new URL(routeValue, window.location.origin);
  } catch {
    return;
  }
  if (route.origin !== window.location.origin) return;

  for (const [name, value] of route.searchParams) {
    if (name === 'return_to' || name === 'login_hint' || name === 'login_hint_mode') continue;
    const field = document.createElement('input');
    field.type = 'hidden';
    field.name = name;
    field.value = value;
    field.dataset.nwpAccountRouteParam = '';
    form.append(field);
  }
}

function closeDetails(details) {
  const disclosure = details?.parentElement;
  if (typeof disclosure?.toggle === 'function') {
    disclosure.toggle(false);
  } else {
    details?.removeAttribute('open');
  }
}

function initForm(form) {
  if (!form || initialized.has(form)) return null;
  initialized.add(form);

  const input = form.querySelector('.nwp-account__input');
  const submit = form.querySelector('.nwp-account__submit');
  const error = form.querySelector('.nwp-account__error');
  if (!input || !submit) return null;
  resetSubmission(form);

  const invalidMessage = form.dataset.invalidMessage || 'Enter a valid email address.';

  /* JS owns validation messaging from here; the native bubble stays the
     no-JS fallback (novalidate is not in the markup on purpose). */
  form.setAttribute('novalidate', '');
  setReturnTo(form);
  setPlatformRouteParameters(form);

  const syncHasValue = () => form.classList.toggle('nwp-has-value', input.value.trim() !== '');
  const clearInvalid = () => {
    if (!form.classList.contains('is-invalid')) return;
    form.classList.remove('is-invalid');
    input.removeAttribute('aria-invalid');
    if (error) error.textContent = '';
  };
  const showInvalid = () => {
    form.classList.add('is-invalid');
    input.setAttribute('aria-invalid', 'true');
    if (error) error.textContent = invalidMessage;
    input.focus({ preventScroll: true });
  };

  syncHasValue();
  input.addEventListener('input', () => {
    syncHasValue();
    clearInvalid();
  });

  form.addEventListener('submit', (event) => {
    if (form.classList.contains('is-submitting')) {
      event.preventDefault();
      return;
    }
    input.value = input.value.trim();
    if (!input.checkValidity()) {
      event.preventDefault();
      showInvalid();
      return;
    }
    setReturnTo(form);
    setPlatformRouteParameters(form);
    form.classList.add('is-submitting');
    submit.setAttribute('aria-busy', 'true');
    /* Disable after the current task so the submission that is already in
       flight is never cancelled by a disabled submitter. */
    setTimeout(() => {
      if (form.classList.contains('is-submitting')) submit.disabled = true;
    }, 0);
    recoveryDeadlines.set(form, Date.now() + 8000);
    recoveryTimers.set(form, setTimeout(() => {
      // A cancelled navigation or 204 response never leaves the document.
      // Offer an explicit retry after this navigation deadline; never resubmit.
      if (form.isConnected && document.visibilityState === 'visible') resetSubmission(form);
    }, 8000));
  });

  return { form, input, submit };
}

function initHeaderPanel(root) {
  const details = root.querySelector('.nwp-account__disclosure');
  if (!details || initialized.has(details)) return;
  initialized.add(details);

  const summary = details.querySelector(':scope > .nwp-account__trigger');
  const panel = details.querySelector(':scope > .nwp-account__panel');
  if (!summary || !panel) return;

  // nwp-header.js owns geometry before native opening and on resize.
  const controls = initForm(panel.querySelector('.nwp-account__form'));
  const input = controls?.input ?? null;

  const isExpanded = () => details.getAttribute('aria-expanded') === 'true';
  const firstFocusable = () =>
    input ??
    panel.querySelector('a[href], button:not([disabled]), input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])');

  let entryFocusFrame = 0;
  const cancelEntryFocus = () => {
    cancelAnimationFrame(entryFocusFrame);
    entryFocusFrame = 0;
  };
  const close = () => {
    cancelEntryFocus();
    closeDetails(details);
  };

  /* A form is "engaged" while it has focus or content: the customer is
     typing and a pointer that wanders off the panel must not close it. */
  const engaged = () => !!input && (document.activeElement === input || details.dataset.nwpAccountEngaged === 'true');
  input?.addEventListener('input', () => { details.dataset.nwpAccountEngaged = String(input.value.trim() !== ''); });

  /* Prestige listens to mouseout on the <details> (bubble phase). Swallowing
     it in the capture phase while engaged keeps the panel open; Escape,
     the sibling-menu handoff and the explicit outside click below remain
     the ways out. */
  details.addEventListener(
    'mouseout',
    (event) => {
      if (engaged() && isExpanded()) event.stopImmediatePropagation();
    },
    { capture: true },
  );

  /* When the field is left empty and the pointer is already gone, resume
     Prestige's normal hover-close after its own tolerance. */
  input?.addEventListener('focusout', () => {
    if (input.value.trim() !== '') return;
    setTimeout(() => {
      if (!isExpanded() || details.getAttribute('aria-expanded') !== 'true') return;
      if (details.parentElement?.trigger !== 'hover') return;
      if (engaged()) return;
      if (details.contains(document.activeElement)) return;
      if (finePointer.matches && details.matches(':hover')) return;
      close();
    }, HOVER_TOLERANCE_MS);
  });

  /* Keyboard: Tab from the open trigger enters the panel deterministically
     (Safari may skip links/inputs in plain Tab order); Enter/Space on the
     trigger (event.detail === 0) lands on the email field after Prestige
     has opened the panel. Hover-open never moves focus. */
  summary.addEventListener('keydown', (event) => {
    cancelEntryFocus();
    if (event.key !== 'Tab' || event.shiftKey || !isExpanded()) return;
    const target = firstFocusable();
    if (!target) return;
    event.preventDefault();
    target.focus({ preventScroll: true });
  });

  let touchStartedClosed = false;
  summary.addEventListener('pointerdown', event => {
    cancelEntryFocus();
    touchStartedClosed = event.pointerType === 'touch' && !isExpanded();
  });
  summary.addEventListener('click', (event) => {
    cancelEntryFocus();
    if ((event.detail === 0 && isExpanded()) || (touchStartedClosed && isExpanded())) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (event.detail === 0) close();
      touchStartedClosed = false;
      return;
    }
    touchStartedClosed = false;
    if (event.detail !== 0) return; // Pointer clicks use the native disclosure toggle.
    entryFocusFrame = requestAnimationFrame(() => {
      entryFocusFrame = 0;
      if (!isExpanded()) return;
      if (!summary.matches(focusVisibleSelector) && document.activeElement !== summary) return;
      firstFocusable()?.focus({ preventScroll: true });
    });
  }, { capture: true });
}

/* Global handlers are delegated and retain no section nodes, so Theme Editor
   unload/reload cycles cannot accumulate detached-form closures. */
document.addEventListener(
  'pointerdown',
  (event) => {
    if (!(event.target instanceof Node)) return;
    for (const details of document.querySelectorAll('.nwp-account__disclosure[open]')) {
      if (details.contains(event.target)) continue;
      closeDetails(details);
    }
  },
  { capture: true },
);

document.addEventListener('toggle', event => {
  if (event.target.matches?.('.nwp-account__disclosure') && !event.target.open) delete event.target.dataset.nwpAccountEngaged;
}, true);
window.addEventListener('pageshow', () => {
  for (const form of document.querySelectorAll('.nwp-account__form.is-submitting')) {
    resetSubmission(form);
  }
});
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState !== 'visible') return;
  for (const form of document.querySelectorAll('.nwp-account__form.is-submitting')) {
    if (Date.now() >= (recoveryDeadlines.get(form) ?? Infinity)) resetSubmission(form);
  }
});

function init(root) {
  const scope = root instanceof Element ? root : document;
  scope.querySelectorAll('.nwp-account--header').forEach((node) => initHeaderPanel(node));
  scope.querySelectorAll('.nwp-account--sidebar .nwp-account__form').forEach((form) => initForm(form));
}

init(document);
document.addEventListener('shopify:section:load', (event) => init(event.target));
